import { create } from 'zustand';

import { v4 as uuidv4 } from 'uuid';

import {
  deleteComment,
  getComments,
  sendComment,
  updateComment,
} from '@/services/api/comments';

import type {
  CommentInfoType,
  DeleteCommentRequest,
  SendCommentRequest,
  UpdateCommentRequest,
} from '@/types/api/comments';

import { useAuthStore } from '@/store';

interface CommentState {
  // Comment data

  comments: Record<string, CommentInfoType[]>; // storyId -> comments

  totalCounts: Record<string, number>; // storyId -> totalCount

  isLoading: Record<string, boolean>; // storyId -> isLoading

  isLoadingMore: Record<string, boolean>; // storyId -> isLoadingMore

  currentPages: Record<string, number>; // storyId -> currentPage

  isLoadingPrevious: Record<string, boolean>;

  // Actions

  fetchComments: (
    storyId: string,

    limit: number,

    page: number,

    focusedCommentId?: string
  ) => Promise<void>;

  loadMoreComments: (
    storyId: string,

    limit: number,

    focusedCommentId?: string
  ) => Promise<void>;

  addComment: (payload: SendCommentRequest) => Promise<void>;

  updateComment: (payload: UpdateCommentRequest) => Promise<void>;

  deleteComment: (payload: DeleteCommentRequest) => Promise<void>;

  loadPreviousComments: (storyId: string, limit: number) => Promise<void>;

  // Helper methods

  getCommentsForStory: (storyId: string) => CommentInfoType[];

  getTotalCountForStory: (storyId: string) => number;

  hasMoreComments: (storyId: string) => boolean;

  isLoadingCommentsForStory: (storyId: string) => boolean;

  isLoadingMoreCommentsForStory: (storyId: string) => boolean;

  isLoadingPreviousCommentsForStory: (storyId: string) => boolean;

  hasPreviousComments: (storyId: string) => boolean;
}

export const useCommentStore = create<CommentState>((set, get) => ({
  comments: {},

  totalCounts: {},

  isLoading: {},

  isLoadingMore: {},

  currentPages: {},

  isLoadingPrevious: {},

  fetchComments: async (storyId, limit, page, focusedCommentId) => {
    set((state) => ({
      isLoading: { ...state.isLoading, [storyId]: true },
    }));

    try {
      const response = await getComments({
        storyId,
        limit,
        page,
        focusedCommentId,
      });

      // Update the current page based on the response when focusedCommentId is provided
      const pageToSet = focusedCommentId ? response.page || page : page;

      set((state) => ({
        comments: {
          ...state.comments,
          [storyId]: response.data || [],
        },
        totalCounts: {
          ...state.totalCounts,
          [storyId]: response.totalCount || 0,
        },
        currentPages: {
          ...state.currentPages,
          [storyId]: pageToSet, // Use the page from response when available
        },
        isLoading: {
          ...state.isLoading,
          [storyId]: false,
        },
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      set((state) => ({
        isLoading: { ...state.isLoading, [storyId]: false },
      }));
    }
  },

  loadMoreComments: async (storyId, limit, focusedCommentId) => {
    const currentPage = get().currentPages[storyId] || 1;

    const nextPage = currentPage + 1;

    // Set loading more state

    set((state) => ({
      isLoadingMore: { ...state.isLoadingMore, [storyId]: true },
    }));

    try {
      const result = await getComments({
        storyId,

        limit,

        page: nextPage,

        focusedCommentId,
      });

      if (result?.data) {
        // Get existing comments for this story

        const existingComments = get().comments[storyId] || [];

        // Get IDs of all existing comments to avoid duplicates

        const existingIds = new Set(existingComments.map((c) => c.id));

        // Filter out any comments that already exist

        const newComments = result.data.filter(
          (comment) => !existingIds.has(comment.id)
        );

        set((state) => ({
          comments: {
            ...state.comments,

            [storyId]: [...existingComments, ...newComments],
          },

          currentPages: {
            ...state.currentPages,

            [storyId]: nextPage,
          },

          isLoadingMore: {
            ...state.isLoadingMore,

            [storyId]: false,
          },
        }));
      }
    } catch (error) {
      console.error('Error loading more comments:', error);

      set((state) => ({
        isLoadingMore: { ...state.isLoadingMore, [storyId]: false },
      }));
    }
  },

  addComment: async (payload) => {
    const { storyId, parentCommentId, content } = payload;

    const user = useAuthStore.getState().user;

    // Create optimistic comment

    const optimisticComment: CommentInfoType = {
      id: `temp-${uuidv4()}`,
      storyId,
      tenantId: '',
      user: user
        ? {
            fullName: user.fullName,
            userName: user.userName,
            email: user.email,
            avatarUrl: '',
            id: user.id ?? '',
          }
        : null,
      parentCommentId,
      content,
      createdDate: new Date().toISOString(),
      updatedDate: new Date().toISOString(),
      story: {
        id: storyId,
        slug: '',
        title: '',
      },
      subComments: [],
      isOptimistic: true,
    };

    // Add optimistic comment to state

    set((state) => {
      const storyComments = state.comments[storyId] || [];

      if (!parentCommentId) {
        // Add to root level and update the story's comment count

        // This is an optimistic update for the UI

        const storyEvent = new CustomEvent('story:commentCountUpdated', {
          detail: { storyId, increment: 1 },
        });

        window.dispatchEvent(storyEvent);

        return {
          comments: {
            ...state.comments,

            [storyId]: [optimisticComment, ...storyComments],
          },
        };
      } else {
        // Add to nested level - recursive function to find and update the parent

        const updateCommentTree = (
          commentsList: CommentInfoType[]
        ): CommentInfoType[] => {
          return commentsList.map((comment) => {
            if (comment.id === parentCommentId) {
              // Found the parent, add the new comment to its subComments

              return {
                ...comment,

                subComments: [
                  optimisticComment,

                  ...(comment.subComments || []),
                ],
              };
            } else if (comment.subComments && comment.subComments.length > 0) {
              // Check in subComments

              return {
                ...comment,

                subComments: updateCommentTree(comment.subComments),
              };
            }

            return comment;
          });
        };

        return {
          comments: {
            ...state.comments,

            [storyId]: updateCommentTree(storyComments),
          },
        };
      }
    });

    try {
      // Send the actual comment to the server

      const result = await sendComment(payload);

      // Remove optimistic comment and add the real one

      set((state) => {
        const storyComments = state.comments[storyId] || [];

        // Remove the optimistic comment

        const removeOptimisticComment = (
          commentsList: CommentInfoType[]
        ): CommentInfoType[] => {
          return commentsList

            .filter((comment) => comment.id !== optimisticComment.id)

            .map((comment) => {
              if (comment.subComments && comment.subComments.length > 0) {
                return {
                  ...comment,

                  subComments: removeOptimisticComment(comment.subComments),
                };
              }

              return comment;
            });
        };

        const commentsWithoutOptimistic =
          removeOptimisticComment(storyComments);

        // Add the real comment

        if (!parentCommentId) {
          // Add to root level

          return {
            comments: {
              ...state.comments,

              [storyId]: [result.data, ...commentsWithoutOptimistic],
            },

            totalCounts: {
              ...state.totalCounts,

              [storyId]: (state.totalCounts[storyId] || 0) + 1,
            },
          };
        } else {
          // Add to nested level

          const addRealComment = (
            commentsList: CommentInfoType[]
          ): CommentInfoType[] => {
            return commentsList.map((comment) => {
              if (comment.id === parentCommentId) {
                return {
                  ...comment,

                  subComments: [result.data, ...(comment.subComments || [])],
                };
              } else if (
                comment.subComments &&
                comment.subComments.length > 0
              ) {
                return {
                  ...comment,

                  subComments: addRealComment(comment.subComments),
                };
              }

              return comment;
            });
          };

          return {
            comments: {
              ...state.comments,

              [storyId]: addRealComment(commentsWithoutOptimistic),
            },

            totalCounts: {
              ...state.totalCounts,

              [storyId]: (state.totalCounts[storyId] || 0) + 1,
            },
          };
        }
      });
    } catch (error) {
      console.error('Error adding comment:', error);

      // Remove the optimistic comment on error

      set((state) => {
        const storyComments = state.comments[storyId] || [];

        const removeOptimisticComment = (
          commentsList: CommentInfoType[]
        ): CommentInfoType[] => {
          return commentsList

            .filter((comment) => comment.id !== optimisticComment.id)

            .map((comment) => {
              if (comment.subComments && comment.subComments.length > 0) {
                return {
                  ...comment,

                  subComments: removeOptimisticComment(comment.subComments),
                };
              }

              return comment;
            });
        };

        return {
          comments: {
            ...state.comments,

            [storyId]: removeOptimisticComment(storyComments),
          },
        };
      });
    }
  },

  updateComment: async (payload) => {
    const { commentId, content } = payload;

    // Find the storyId for this comment

    let storyId = '';

    const allStoryIds = Object.keys(get().comments);

    for (const id of allStoryIds) {
      const findCommentStoryId = (commentsList: CommentInfoType[]): boolean => {
        for (const comment of commentsList) {
          if (comment.id === commentId) {
            storyId = id;

            return true;
          }

          if (comment.subComments && comment.subComments.length > 0) {
            if (findCommentStoryId(comment.subComments)) {
              return true;
            }
          }
        }

        return false;
      };

      if (findCommentStoryId(get().comments[id] || [])) {
        break;
      }
    }

    if (!storyId) {
      console.error('Could not find storyId for comment:', commentId);

      return;
    }

    // Update comment optimistically

    set((state) => {
      const storyComments = state.comments[storyId] || [];

      const updateCommentInTree = (
        commentsList: CommentInfoType[]
      ): CommentInfoType[] => {
        return commentsList.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,

              content,

              updatedDate: new Date().toISOString(),

              isOptimistic: true,
            };
          } else if (comment.subComments && comment.subComments.length > 0) {
            return {
              ...comment,

              subComments: updateCommentInTree(comment.subComments),
            };
          }

          return comment;
        });
      };

      return {
        comments: {
          ...state.comments,

          [storyId]: updateCommentInTree(storyComments),
        },
      };
    });

    try {
      // Send the update to the server

      const result = await updateComment(payload);

      // Update with the real data from the server

      set((state) => {
        const storyComments = state.comments[storyId] || [];

        const finalizeCommentUpdate = (
          commentsList: CommentInfoType[]
        ): CommentInfoType[] => {
          return commentsList.map((comment) => {
            if (comment.id === commentId) {
              return {
                ...comment,

                ...result.data,

                isOptimistic: false,
              };
            } else if (comment.subComments && comment.subComments.length > 0) {
              return {
                ...comment,

                subComments: finalizeCommentUpdate(comment.subComments),
              };
            }

            return comment;
          });
        };

        return {
          comments: {
            ...state.comments,

            [storyId]: finalizeCommentUpdate(storyComments),
          },
        };
      });
    } catch (error) {
      console.error('Error updating comment:', error);

      // Revert the optimistic update on error

      get().fetchComments(storyId, 10, 1); // Refetch to get the correct state
    }
  },

  deleteComment: async (payload) => {
    const { commentId } = payload;

    // Find the storyId for this comment

    let storyId = '';

    const allStoryIds = Object.keys(get().comments);

    for (const id of allStoryIds) {
      const findCommentStoryId = (commentsList: CommentInfoType[]): boolean => {
        for (const comment of commentsList) {
          if (comment.id === commentId) {
            storyId = id;

            return true;
          }

          if (comment.subComments && comment.subComments.length > 0) {
            if (findCommentStoryId(comment.subComments)) {
              return true;
            }
          }
        }

        return false;
      };

      if (findCommentStoryId(get().comments[id] || [])) {
        break;
      }
    }

    if (!storyId) {
      console.error('Could not find storyId for comment:', commentId);

      return;
    }

    // Check if this is a root comment by looking at the comment structure

    let isRootComment = false;

    const findCommentAndCheckIfRoot = (
      commentsList: CommentInfoType[]
    ): boolean => {
      for (const comment of commentsList) {
        if (comment.id === commentId) {
          // If the comment has no parentCommentId, it's a root comment

          isRootComment = !comment.parentCommentId;

          return true;
        }

        if (comment.subComments && comment.subComments.length > 0) {
          if (findCommentAndCheckIfRoot(comment.subComments)) {
            return true;
          }
        }
      }

      return false;
    };

    findCommentAndCheckIfRoot(get().comments[storyId] || []);

    // If it's a root comment, dispatch an event to decrement the comment count

    if (isRootComment) {
      const storyEvent = new CustomEvent('story:commentCountUpdated', {
        detail: { storyId, increment: -1 },
      });

      window.dispatchEvent(storyEvent);
    }

    // Mark comment as being deleted

    set((state) => {
      const storyComments = state.comments[storyId] || [];

      const markCommentAsDeleting = (
        commentsList: CommentInfoType[]
      ): CommentInfoType[] => {
        return commentsList.map((comment) => {
          if (comment.id === commentId) {
            return {
              ...comment,

              isDeleting: true,
            };
          } else if (comment.subComments && comment.subComments.length > 0) {
            return {
              ...comment,

              subComments: markCommentAsDeleting(comment.subComments),
            };
          }

          return comment;
        });
      };

      return {
        comments: {
          ...state.comments,

          [storyId]: markCommentAsDeleting(storyComments),
        },
      };
    });

    try {
      // Send the delete request to the server

      await deleteComment(payload);

      // Remove the comment from the state

      set((state) => {
        const storyComments = state.comments[storyId] || [];

        const removeCommentFromTree = (
          commentsList: CommentInfoType[]
        ): CommentInfoType[] => {
          return commentsList

            .filter((comment) => comment.id !== commentId)

            .map((comment) => {
              if (comment.subComments && comment.subComments.length > 0) {
                return {
                  ...comment,

                  subComments: removeCommentFromTree(comment.subComments),
                };
              }

              return comment;
            });
        };

        return {
          comments: {
            ...state.comments,

            [storyId]: removeCommentFromTree(storyComments),
          },

          totalCounts: {
            ...state.totalCounts,

            [storyId]: Math.max(0, (state.totalCounts[storyId] || 0) - 1),
          },
        };
      });
    } catch (error) {
      console.error('Error deleting comment:', error);

      // Revert the optimistic deletion on error

      get().fetchComments(storyId, 10, 1); // Refetch to get the correct state
    }
  },

  loadPreviousComments: async (storyId, limit) => {
    const currentPage = get().currentPages[storyId] || 1;
    if (currentPage <= 1) return;

    const previousPage = currentPage - 1;

    set((state) => ({
      isLoadingPrevious: { ...state.isLoadingPrevious, [storyId]: true },
    }));

    try {
      const result = await getComments({
        storyId,
        limit,
        page: previousPage,
      });

      if (result?.data) {
        const existingComments = get().comments[storyId] || [];
        const existingIds = new Set(existingComments.map((c) => c.id));
        const newComments = result.data.filter(
          (comment) => !existingIds.has(comment.id)
        );

        set((state) => ({
          comments: {
            ...state.comments,
            [storyId]: [...newComments, ...existingComments],
          },
          currentPages: {
            ...state.currentPages,
            [storyId]: previousPage,
          },
          isLoadingPrevious: {
            ...state.isLoadingPrevious,
            [storyId]: false,
          },
        }));
      }
    } catch (error) {
      console.error('Error loading previous comments:', error);
      set((state) => ({
        isLoadingPrevious: { ...state.isLoadingPrevious, [storyId]: false },
      }));
    }
  },

  // Helper methods

  getCommentsForStory: (storyId) => {
    return get().comments[storyId] || [];
  },

  getTotalCountForStory: (storyId) => {
    return get().totalCounts[storyId] || 0;
  },

  hasMoreComments: (storyId) => {
    const comments = get().getCommentsForStory(storyId);
    const totalCount = get().getTotalCountForStory(storyId);
    const currentPage = get().currentPages[storyId] || 1;
    const limit = 10; // COMMENT_LIMIT

    // Calculate how many root comments we should have loaded by now
    const expectedLoadedCount = currentPage * limit;

    // Only count root-level comments (not subcomments)
    const rootCommentsCount = comments.filter(
      (comment) => !comment.parentCommentId
    ).length;

    // We have more comments if:
    // 1. The total count is greater than what we've loaded so far based on pagination
    // 2. And we haven't loaded all comments yet
    return totalCount > expectedLoadedCount && rootCommentsCount < totalCount;
  },

  isLoadingCommentsForStory: (storyId) => {
    return get().isLoading[storyId] || false;
  },

  isLoadingMoreCommentsForStory: (storyId) => {
    return get().isLoadingMore[storyId] || false;
  },

  isLoadingPreviousCommentsForStory: (storyId) => {
    return get().isLoadingPrevious[storyId] || false;
  },

  hasPreviousComments: (storyId) => {
    const currentPage = get().currentPages[storyId] || 1;
    return currentPage > 1;
  },
}));
